import React from 'react';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import styles from './Pagination.module.scss';

const Pagination = ({ pages, prevTitle, nextTitle }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const qs = queryString.parse(location.search);
  const getLink = (page) => {
    if (page > 1) {
      return `?${queryString.stringify({ ...qs, page })}`.replace(/%3A/g, ':');
    }

    const { page: _curr, ...rest } = qs;
    return `?${queryString.stringify(rest)}`.replace(/%3A/g, ':');
  };

  const disabled = event => event.preventDefault();

  const currentPage = ~~qs.page || 1;
  const prevButtonTitle = prevTitle === null ? t('pagination.prev') : prevTitle;
  const nextButtonTitle = nextTitle === null ? t('pagination.next') : nextTitle;

  if (pages < 2) {
    return null;
  }

  return (
    <div className={styles.pagination}>
      <ul>
        <li className={styles.prev}>
          {currentPage - 1 > 0 ? (
            <Link
              rel="nofollow"
              to={getLink(currentPage - 1)}
              className={prevButtonTitle ? styles.prevWithTitle : null}
            >
              <i className="la la-long-arrow-left" />
              {prevButtonTitle}
            </Link>
          ) : (
            <Link
              rel="nofollow"
              className={cn(styles.disabled, { [styles.prevWithTitle]: !!prevButtonTitle })}
              to={getLink(currentPage)}
              onClick={disabled}
            >
              <i className="la la-long-arrow-left" />
              {prevButtonTitle}
            </Link>
          )}
        </li>
        {(currentPage - 4 > 0 && currentPage + 1 > pages) && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage - 4)}>{currentPage - 4}</Link>
          </li>
        )}
        {(currentPage - 3 > 0 && currentPage + 2 > pages) && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage - 3)}>{currentPage - 3}</Link>
          </li>
        )}
        {currentPage - 2 > 0 && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage - 2)}>{currentPage - 2}</Link>
          </li>
        )}
        {currentPage - 1 > 0 && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage - 1)}>{currentPage - 1}</Link>
          </li>
        )}
        <li className={`${styles.active} ${styles.number}`}>
          <Link rel="nofollow" to={getLink(currentPage)}>{currentPage}</Link>
        </li>
        {currentPage + 1 <= pages && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage + 1)}>{currentPage + 1}</Link>
          </li>
        )}
        {currentPage + 2 <= pages && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage + 2)}>{currentPage + 2}</Link>
          </li>
        )}
        {(currentPage + 3 <= pages && currentPage < 3) && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage + 3)}>{currentPage + 3}</Link>
          </li>
        )}
        {(currentPage + 4 <= pages && currentPage < 2) && (
          <li className={styles.number}>
            <Link rel="nofollow" to={getLink(currentPage + 4)}>{currentPage + 4}</Link>
          </li>
        )}
        {/* <li><span className={styles.delimiter}>...</span></li> */}
        <li className={styles.next}>
          {currentPage + 1 <= pages ? (
            <Link
              rel="nofollow"
              to={getLink(currentPage + 1)}
              className={nextButtonTitle ? styles.nextWithTitle : null}
            >
              {nextButtonTitle}
              <i className="la la-long-arrow-right" />
            </Link>
          ) : (
            <Link
              rel="nofollow"
              className={cn(styles.disabled, { [styles.nextWithTitle]: !!nextButtonTitle })}
              to={getLink(currentPage)}
              onClick={disabled}
            >
              {nextButtonTitle}
              <i className="la la-long-arrow-right" />
            </Link>
          )}
        </li>
      </ul>
    </div>
  );
};

Pagination.propTypes = {
  prevTitle: PropTypes.string,
  nextTitle: PropTypes.string,
  pages: PropTypes.number,
};

Pagination.defaultProps = {
  prevTitle: null,
  nextTitle: null,
  pages: 1,
};

export default Pagination;
